import { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Fetch active, publicly visible promotions for a given surface. Filters
 * happen server-side (`/api/promotions?surface=X` already restricts to
 * status=active + display flag + scheduled window). Sorted by
 * `display.display_priority` desc.
 *
 * @param {string} surface  one of `homepage`, `announcement_bar`,
 *   `promotions_page`, `product_pages`, `product_catalog`, `cart`,
 *   `checkout`.
 * @returns {{promos: Array, loading: boolean}}
 */
export function useActivePromotions(surface) {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/promotions?surface=${encodeURIComponent(surface)}`);
        if (!res.ok) return;
        const list = await res.json();
        if (!cancelled) setPromos(Array.isArray(list) ? list : []);
      } catch {
        // silent — surface just won't render
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [surface]);

  return { promos, loading };
}

/**
 * Given a cart-surface promo and current subtotal + line-items, compute:
 *   - eligibleSubtotal: portion of `subtotal` that counts toward the
 *     promo threshold (single-vial only if promo excludes ten_packs)
 *   - remaining: dollars still needed to unlock the promo
 *   - progressPct: 0..100 progress toward `minimum_qualifying_subtotal`
 *   - qualifies: boolean, true when threshold met AND at least one
 *     eligible item is in the cart
 *
 * `items` is the CartContext cart items list (each has packType which is
 * either `single` or `10pack`).
 */
export function computeCartProgress(promo, items) {
  if (!promo || !Array.isArray(items) || items.length === 0) {
    return { eligibleSubtotal: 0, remaining: 0, progressPct: 0, qualifies: false };
  }
  const excludeTenPacks = !!promo.eligibility?.exclude_ten_packs
    || (promo.eligibility?.excluded_package_types || []).includes('ten_pack');

  const eligibleSubtotal = items.reduce((sum, it) => {
    if (excludeTenPacks && it.packType === '10pack') return sum;
    return sum + Number(it.price || 0) * Number(it.quantity || 0);
  }, 0);

  const threshold = Number(promo.discount?.minimum_qualifying_subtotal || 0);
  const remaining = Math.max(0, threshold - eligibleSubtotal);
  const progressPct = threshold > 0
    ? Math.min(100, Math.round((eligibleSubtotal / threshold) * 100))
    : (eligibleSubtotal > 0 ? 100 : 0);
  const qualifies = eligibleSubtotal >= threshold && eligibleSubtotal > 0;

  return { eligibleSubtotal, remaining, progressPct, qualifies };
}

/**
 * PDP eligibility check. Given a promo and a product record, returns
 * `{ eligible: bool, note: string|null }` for rendering a small notice
 * on the product page.
 */
export function computePdpEligibility(promo, product) {
  if (!promo || !product) return { eligible: false, note: null };
  const elig = promo.eligibility || {};
  const catSlug = product.category || '';

  // Explicit exclusions win over inclusions.
  if ((elig.excluded_product_slugs || []).includes(product.slug)) return { eligible: false, note: null };
  if ((elig.excluded_category_slugs || []).includes(catSlug)) return { eligible: false, note: null };

  let eligible = false;
  if (elig.all_products) {
    eligible = true;
  } else if ((elig.included_product_slugs || []).includes(product.slug)) {
    eligible = true;
  } else if ((elig.included_category_slugs || []).includes(catSlug)) {
    eligible = true;
  }

  if (!eligible) return { eligible: false, note: null };

  const excludeTenPacks = !!elig.exclude_ten_packs
    || (elig.excluded_package_types || []).includes('ten_pack');
  const note = excludeTenPacks
    ? 'Applies to single-vial purchase only — 10-pack excluded.'
    : null;
  return { eligible: true, note };
}
